import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, ChevronLeft, ChevronRight, Calendar, ArrowUpRight, Palette } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Compact Monday–Sunday weekly view for the dashboard.
 * Groups this week's appointments by day, click any day to jump to that
 * day on /admin/calendar. Prev/Next week nav is intentionally lightweight
 * — the full page is one click away.
 */

const DOW_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function startOfWeekMonday(d) {
  const date = new Date(d);
  date.setHours(0, 0, 0, 0);
  // JS: 0 = Sun, 1 = Mon ... shift so Monday = 0
  const day = (date.getDay() + 6) % 7;
  date.setDate(date.getDate() - day);
  return date;
}

function addDays(d, n) {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

function ymd(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const STATUS_TONE = {
  Scheduled: 'bg-blue-100 text-blue-700 border-l-blue-500',
  'In Progress': 'bg-amber-100 text-amber-700 border-l-amber-500',
  Completed: 'bg-emerald-100 text-emerald-700 border-l-emerald-500',
  Canceled: 'bg-zinc-200 text-zinc-500 line-through border-l-zinc-400',
  Booked: 'bg-blue-100 text-blue-700 border-l-blue-500',
};

// Color palette assigned by hashing the service name — gives every service
// type a stable, distinct look without any config.
const SERVICE_ACCENTS = [
  { class: 'border-l-[#F56F05]',   hex: '#F56F05', name: 'orange' },
  { class: 'border-l-purple-500',  hex: '#a855f7', name: 'purple' },
  { class: 'border-l-teal-500',    hex: '#14b8a6', name: 'teal' },
  { class: 'border-l-pink-500',    hex: '#ec4899', name: 'pink' },
  { class: 'border-l-indigo-500',  hex: '#6366f1', name: 'indigo' },
  { class: 'border-l-lime-600',    hex: '#65a30d', name: 'lime' },
  { class: 'border-l-cyan-500',    hex: '#06b6d4', name: 'cyan' },
  { class: 'border-l-rose-500',    hex: '#f43f5e', name: 'rose' },
];

function accentForService(name) {
  if (!name) return SERVICE_ACCENTS[0];
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  return SERVICE_ACCENTS[hash % SERVICE_ACCENTS.length];
}

const MAX_ITEMS_PER_DAY = 5;

const WeekAtGlanceWidget = ({ token }) => {
  const [weekStart, setWeekStart] = useState(() => startOfWeekMonday(new Date()));
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [legendOpen, setLegendOpen] = useState(false);

  const weekEnd = useMemo(() => addDays(weekStart, 7), [weekStart]);
  const todayYmd = ymd(new Date());

  const fetchWeek = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/appointments`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { start_date: weekStart.toISOString(), end_date: weekEnd.toISOString() },
      });
      setAppointments(res.data || []);
    } catch {
      setAppointments([]);
    } finally {
      setLoading(false);
    }
  }, [token, weekStart, weekEnd]);

  useEffect(() => { fetchWeek(); }, [fetchWeek]);

  // Bucket appointments by YYYY-MM-DD
  const byDay = useMemo(() => {
    const m = {};
    for (let i = 0; i < 7; i += 1) m[ymd(addDays(weekStart, i))] = [];
    for (const a of appointments) {
      const key = (a.start_time || '').slice(0, 10);
      if (m[key]) m[key].push(a);
    }
    // Sort each day by time
    Object.values(m).forEach(arr => arr.sort((x, y) => (x.start_time || '').localeCompare(y.start_time || '')));
    return m;
  }, [appointments, weekStart]);

  const rangeLabel = () => {
    const opts = { month: 'short', day: 'numeric' };
    const startStr = weekStart.toLocaleDateString(undefined, opts);
    const endStr = addDays(weekStart, 6).toLocaleDateString(undefined, { ...opts, year: weekStart.getFullYear() !== addDays(weekStart, 6).getFullYear() ? 'numeric' : undefined });
    return `${startStr} – ${endStr}`;
  };

  const isThisWeek = ymd(weekStart) === ymd(startOfWeekMonday(new Date()));

  // Unique services appearing this week — becomes the legend
  const serviceLegend = useMemo(() => {
    const seen = new Map();
    for (const a of appointments) {
      const name = a.service?.name || a.service_name;
      if (!name || seen.has(name)) continue;
      seen.set(name, accentForService(name));
    }
    return Array.from(seen.entries()).map(([name, accent]) => ({ name, accent }));
  }, [appointments]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl border border-zinc-200 shadow-md overflow-hidden"
      data-testid="week-at-glance"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-100 bg-gradient-to-r from-zinc-50 to-white">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#F56F05] to-[#C2410C] flex items-center justify-center shadow-lg">
            <Calendar className="w-4 h-4 text-white" />
          </div>
          <div>
            <p className="text-sm font-bold text-zinc-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Week at a glance</p>
            <p className="text-xs text-zinc-500" data-testid="week-range">{rangeLabel()}</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {serviceLegend.length > 0 && (
            <button
              type="button"
              onClick={() => setLegendOpen(o => !o)}
              className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-lg transition-colors ${
                legendOpen ? 'bg-orange-100 text-[#F56F05]' : 'text-zinc-500 hover:bg-zinc-100 hover:text-zinc-800'
              }`}
              title="Service color key"
              data-testid="legend-toggle-btn"
            >
              <Palette className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Legend</span>
            </button>
          )}
          <button
            onClick={() => setWeekStart(addDays(weekStart, -7))}
            className="p-1.5 rounded-lg hover:bg-zinc-100 text-zinc-500 hover:text-zinc-800"
            title="Previous week"
            data-testid="prev-week-btn"
          ><ChevronLeft className="w-4 h-4" /></button>
          {!isThisWeek && (
            <button
              onClick={() => setWeekStart(startOfWeekMonday(new Date()))}
              className="px-2 py-1 rounded-lg text-xs font-semibold text-[#F56F05] hover:bg-orange-50"
              data-testid="today-btn"
            >Today</button>
          )}
          <button
            onClick={() => setWeekStart(addDays(weekStart, 7))}
            className="p-1.5 rounded-lg hover:bg-zinc-100 text-zinc-500 hover:text-zinc-800"
            title="Next week"
            data-testid="next-week-btn"
          ><ChevronRight className="w-4 h-4" /></button>
          <Link
            to="/admin/calendar"
            className="ml-2 inline-flex items-center gap-1 text-xs font-semibold text-zinc-600 hover:text-[#F56F05] px-2 py-1 rounded-lg hover:bg-orange-50"
            data-testid="open-calendar-link"
          >
            Full calendar <ArrowUpRight className="w-3 h-3" />
          </Link>
        </div>
      </div>

      {/* Legend panel */}
      <AnimatePresence initial={false}>
        {legendOpen && serviceLegend.length > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden bg-orange-50/40 border-b border-zinc-100"
            data-testid="service-legend-panel"
          >
            <div className="px-5 py-3 flex flex-wrap gap-2">
              <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider w-full">Service color key (this week)</p>
              {serviceLegend.map(({ name, accent }) => (
                <div
                  key={name}
                  className="inline-flex items-center gap-1.5 bg-white border border-zinc-200 rounded-full px-2.5 py-1 text-xs shadow-sm"
                  data-testid={`legend-item-${name.toLowerCase().replace(/\W+/g, '-')}`}
                >
                  <span className="w-3 h-3 rounded-sm" style={{ backgroundColor: accent.hex }} />
                  <span className="font-semibold text-zinc-800">{name}</span>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Week grid */}
      {loading ? (
        <div className="p-10 flex items-center justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-[#F56F05]" />
        </div>
      ) : (
        <div className="grid grid-cols-7 divide-x divide-zinc-100" data-testid="week-grid">
          {DOW_LABELS.map((label, i) => {
            const day = addDays(weekStart, i);
            const key = ymd(day);
            const isToday = key === todayYmd;
            const items = byDay[key] || [];
            return (
              <Link
                key={key}
                to={`/admin/calendar?date=${key}`}
                className={`group flex flex-col min-h-[160px] hover:bg-orange-50/30 transition-colors ${isToday ? 'bg-orange-50/60' : ''}`}
                data-testid={`day-col-${label.toLowerCase()}`}
              >
                <div className={`px-2.5 py-2 border-b border-zinc-100 ${isToday ? 'bg-[#F56F05] text-white' : 'bg-white text-zinc-700'}`}>
                  <p className="text-[10px] font-bold uppercase tracking-wider opacity-70">{label}</p>
                  <p className="text-base font-extrabold" style={{ fontFamily: 'Outfit, sans-serif' }}>{day.getDate()}</p>
                </div>
                <div className="flex-1 p-2 space-y-1.5">
                  {items.length === 0 ? (
                    <p className="text-[10px] text-zinc-300 italic pt-1">—</p>
                  ) : items.slice(0, MAX_ITEMS_PER_DAY).map(a => {
                    const time = new Date(a.start_time).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
                    const serviceName = a.service?.name || a.service_name || '';
                    const cleanerName = a.cleaner_name || a.assigned_cleaner_name || '';
                    const statusTone = STATUS_TONE[a.status] || STATUS_TONE['Booked'];
                    const accent = accentForService(serviceName);
                    return (
                      <div
                        key={a.id}
                        className={`rounded-md px-1.5 py-1 text-[10px] font-medium leading-tight border-l-2 ${statusTone} ${accent.class}`}
                        title={`${time} · ${a.client_name || 'Client'}${cleanerName ? ` · ${cleanerName}` : ''}${serviceName ? ` · ${serviceName}` : ''}`}
                      >
                        <p className="font-bold truncate">{time} · {a.client_name || 'Client'}</p>
                        {cleanerName && <p className="truncate opacity-80">{cleanerName}</p>}
                        {serviceName && <p className="truncate opacity-70 text-[9px] uppercase tracking-wide">{serviceName}</p>}
                      </div>
                    );
                  })}
                  {items.length > MAX_ITEMS_PER_DAY && (
                    <p className="text-[10px] font-semibold text-[#F56F05]" data-testid={`day-overflow-${label.toLowerCase()}`}>
                      +{items.length - MAX_ITEMS_PER_DAY} more
                    </p>
                  )}
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </motion.div>
  );
};

export default WeekAtGlanceWidget;
